import { useState } from 'react';
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import { Box, ContentPanel, Svg, SvgBubble, Text } from '@hopskipdrive/react-components';
import { createInvoiceGroup, deleteInvoiceGroup, updateInvoiceGroup } from '@rideiq/organizers/api/services/Billing';
import { InvoiceGroupFormData } from '@rideiq/organizers/features/InvoiceGroups/InvoiceGroupForm';
import { InvoiceGroupsTable } from '@rideiq/organizers/features/InvoiceGroups/InvoiceGroupsTable';
import { ButtonLink } from '@rideiq/shared/components';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import { colorTokens, variables } from '@rideiq/shared/components/variables';
import { useToastStore } from '@rideiq/shared/stores/toastStore';
import styled from 'styled-components';

const CONTENT_MIN_HEIGHT = `calc(100vh - ${variables.headingHeights.withTitleTabs}px - 1.5rem)`;

type ContextType = {
  isSubmitting: boolean;
  handleSubmit: (data: InvoiceGroupFormData) => void;
  isEditSubmitting: boolean;
  handleEdit: (id: number, data: InvoiceGroupFormData, isDefault: boolean) => void;
  isDeleting: boolean;
  handleDelete: (id: number) => void;
};

export const BillingInvoiceGroups = () => {
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToastStore();

  const handleSubmit = async (data: InvoiceGroupFormData) => {
    setIsSubmitting(true);
    const response = await createInvoiceGroup(data);

    setIsSubmitting(false);

    if (response.type === 'success') {
      addToast({ severity: 'success', message: 'Invoice group created' });
      setShouldRefetch(true);
      navigate('/settings/invoice_groups');
    } else {
      response.error?.errors?.forEach((error) =>
        addToast({ severity: 'error', message: `Invoice group cannot be created. ${error}` })
      );
    }
  };

  const handleEdit = async (id: number, data: InvoiceGroupFormData, isDefault: boolean) => {
    setIsEditSubmitting(true);
    const response = await updateInvoiceGroup(id, data);
    const messagingText = isDefault ? 'Default Invoice group' : 'Invoice group';

    setIsEditSubmitting(false);

    if (response.type === 'success') {
      addToast({ severity: 'success', message: `${messagingText} saved` });
      setShouldRefetch(true);
      navigate('/settings/invoice_groups');
    } else {
      response.error?.errors?.forEach((error) =>
        addToast({ severity: 'error', message: `${messagingText} cannot be saved. ${error}` })
      );
    }
  };

  const handleDelete = async (id: number) => {
    setIsDeleting(true);
    const response = await deleteInvoiceGroup(id);

    setIsDeleting(false);

    if (response.type === 'success') {
      addToast({ severity: 'success', message: 'Invoice group deleted' });
      setShouldRefetch(true);
      navigate('/settings/invoice_groups');
    } else {
      response.error?.errors?.forEach((error) =>
        addToast({ severity: 'error', message: `Invoice group cannot be deleted. ${error}` })
      );
    }
  };

  return (
    <>
      <ContentPanel
        data-testid="billing-invoice-groups"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gridArea: 'content',
          overflow: 'auto',
          padding: '20px',
          gap: '20px',
          marginTop: '1.5rem',
          minHeight: CONTENT_MIN_HEIGHT,
        }}
      >
        <InnerContainer>
          <Box display="flex" flexDirection="column" gap="9px">
            <PageHeading variant="h2">Invoice Groups</PageHeading>
            <Box display="flex" gap="20px" alignItems="center">
              <Text variant="subtitle2" style={{ color: colorTokens.text.medium }}>
                {
                  'Create custom  invoice groups to manage how invoices are done to match your internal accounting requirements.'
                }
              </Text>

              <SvgBubble
                role="button"
                tabIndex={0}
                aria-label="Invoice groups help center"
                style={{ minWidth: '26px' }}
                onClick={() => window.open(window.env.RIDEIQ_INVOICE_GROUPS_HELP_CENTER_URL, '_blank')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    window.open(window.env.RIDEIQ_INVOICE_GROUPS_HELP_CENTER_URL, '_blank');
                  }
                }}
              >
                <Svg icon="question" />
              </SvgBubble>
            </Box>
          </Box>
          <ButtonLink
            color="secondary"
            to="/settings/invoice_groups/new"
            data-testid="create-invoice-group"
            size="large"
            style={{ maxWidth: '212px', marginLeft: 'auto', height: '42px', padding: '8px 31.5px' }}
            aria-label="Create Invoice Group"
          >
            Create Invoice Group
          </ButtonLink>
        </InnerContainer>
        <InvoiceGroupsTable shouldRefetch={shouldRefetch} onRefetched={() => setShouldRefetch(false)} />
      </ContentPanel>
      <Outlet
        context={
          { isSubmitting, handleSubmit, isEditSubmitting, handleEdit, isDeleting, handleDelete } satisfies ContextType
        }
      />
    </>
  );
};

const InnerContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 9px;

  ${variables.mediaQuery.lessThan('desktop')`
    flex-direction: column;
  `}
`;

export function useCreateInvoiceGroup() {
  const { isSubmitting, handleSubmit } = useOutletContext<ContextType>();

  return { isSubmitting, handleSubmit };
}

export function useEditInvoiceGroup() {
  const { isEditSubmitting, handleEdit } = useOutletContext<ContextType>();

  return { isSubmitting: isEditSubmitting, handleSubmit: handleEdit };
}

export function useDeleteInvoiceGroup() {
  const { isDeleting, handleDelete } = useOutletContext<ContextType>();

  return { isDeleting, handleDelete };
}
